import type { CheckoutInitTokenTransferResponse } from '@funkit/api-base'
import { isTransferChainEnabled } from '@funkit/connect-core'
import { useEffect, useMemo, useRef } from 'react'
import { logger } from '../../logging/logger'
import type { AvailableChain } from '../availableChains'
import { depositAssets } from '../depositAssets'
import type { SupportedToken } from '../supportedTokens'

interface UseDepositAssetsOptions {
  tokens: SupportedToken[]
  chains: AvailableChain[]
  metadata: SupportedToken[]
  enabled: boolean
  metadataReady?: boolean
  transferInit?: CheckoutInitTokenTransferResponse
}

export function useDepositAssets({
  tokens,
  chains,
  metadata,
  enabled,
  metadataReady = true,
  transferInit,
}: UseDepositAssetsOptions) {
  const { assets, exclusions } = useMemo(() => {
    if (!enabled) {
      return { assets: [], exclusions: [] }
    }
    return depositAssets(
      tokens,
      chains.filter((chain) =>
        isTransferChainEnabled(Number(chain.id), transferInit),
      ),
      metadata,
    )
  }, [tokens, chains, metadata, enabled, transferInit])
  const warned = useRef(new Set<string>())
  useEffect(() => {
    if (!enabled || !metadataReady) {
      return
    }
    for (const entry of exclusions) {
      const key = `${entry.symbol}:${entry.chainId ?? ''}:${entry.reason}`
      if (!warned.current.has(key)) {
        // Warn once per exclusion for the lifetime of this hook instance.
        warned.current.add(key)
        logger.warn('connect-rn:deposit:asset_excluded', { ...entry })
      }
    }
  }, [enabled, metadataReady, exclusions])
  return assets
}
